// frontend/admin-portal/src/pages/OrderCreate.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Input,
  Alert,
  Spinner,
  formatMoney,
} from "@fiberhome/shared-ui";
import { orderService } from "../services/orderService";
import { resolveImageUrl } from "../services/fileService";
import { useStoreIdentity } from "../hooks/useStoreIdentity";
import CustomerPicker from "../components/orders/CustomerPicker";
import CustomerModal from "../components/orders/CustomerModal";
import OrderItemsEditor from "../components/orders/OrderItemsEditor";

export default function OrderCreate() {
  const navigate = useNavigate();
  const store = useStoreIdentity();

  const [defaults, setDefaults] = useState(null);
  const [defaultsLoading, setDefaultsLoading] = useState(true);

  const [customer, setCustomer] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [form, setForm] = useState({
    kind: "offer",
    title: "",
    currency: "USD",
    validUntil: "",
    discountPercent: 0,
    taxRate: 0,
    shipping: 0,
    notes: "",
    internalNotes: "",
    termsAndConditions: "",
    items: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load defaults from Settings
  useEffect(() => {
    let alive = true;
    orderService
      .getDefaults()
      .then((d) => {
        if (!alive) return;
        setDefaults(d);
        setForm((f) => ({
          ...f,
          currency: d.currency || "USD",
          taxRate: d.taxEnabled ? Number(d.taxRate) || 0 : 0,
          discountPercent: 0,
          validUntil: d.dueDays
            ? (() => {
                const dt = new Date();
                dt.setDate(dt.getDate() + Number(d.dueDays));
                return dt.toISOString().slice(0, 10);
              })()
            : "",
          termsAndConditions: d.invoiceFooter || "",
        }));
      })
      .catch(() => {})
      .finally(() => alive && setDefaultsLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleDiscountChange = (value) => {
    const n = Number(value) || 0;
    if (defaults && !defaults.allowManualDiscount) {
      set("discountPercent", 0);
      return;
    }
    const max = defaults?.maxDiscount ?? 100;
    set("discountPercent", Math.min(Math.max(n, 0), max));
  };

  const subtotal = form.items.reduce((s, it) => {
    const q = Number(it.quantity) || 0;
    const p = Number(it.unitPrice) || 0;
    const d = Number(it.discountPercent) || 0;
    return s + q * p * (1 - d / 100);
  }, 0);
  const discountAmount = subtotal * ((Number(form.discountPercent) || 0) / 100);
  const afterDiscount = subtotal - discountAmount;
  const taxAmount = afterDiscount * ((Number(form.taxRate) || 0) / 100);
  const totalAmount = afterDiscount + taxAmount + (Number(form.shipping) || 0);

  const submit = async () => {
    if (!customer) {
      setError("Pick a customer first");
      return;
    }
    if (form.items.length === 0) {
      setError("Add at least one item");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const order = await orderService.create({
        ...form,
        customerId: customer.id,
        items: form.items.map((it) => ({
          productId: it.productId || null,
          productName: it.productName,
          productSku: it.productSku || null,
          quantity: Number(it.quantity) || 0,
          unit: it.unit || "pcs",
          unitPrice: Number(it.unitPrice) || 0,
          discountPercent: Number(it.discountPercent) || 0,
          notes: it.notes || null,
        })),
      });
      navigate(`/orders/${order.id}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  if (defaultsLoading) {
    return (
      <>
        <PageHeader
          title="New Offer / Order"
          breadcrumbs={["Home", "Sales", "Offers & Orders", "New"]}
        />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  const settingsNote = defaults?.taxEnabled
    ? `Default tax ${defaults.taxRate}%`
    : "Tax disabled in Settings → Finance";
  const discountNote =
    defaults && !defaults.allowManualDiscount
      ? "Manual discounts disabled in Settings → Finance"
      : `Max discount ${defaults?.maxDiscount ?? 100}%`;

  return (
    <>
      <PageHeader
        title="New Offer / Order"
        subtitle={`Store: ${defaults?.store?.name || store.name || "FiberHome"}`}
        breadcrumbs={["Home", "Sales", "Offers & Orders", "New"]}
        actions={
          <Button variant="ghost" onClick={() => navigate("/orders")}>
            Cancel
          </Button>
        }
      />

      {/* ---------- Store identity banner ---------- */}
      <div className="fh-order-store-banner" style={{ marginBottom: "1rem" }}>
        <div className="fh-order-store-banner__left">
          {store.logo ? (
            <img
              src={resolveImageUrl(store.logo)}
              alt={store.name}
              className="fh-order-store-banner__logo"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="fh-order-store-banner__logo-placeholder">
              {store.name?.[0]?.toUpperCase() || "F"}
            </div>
          )}
          <div>
            <div className="fh-order-store-banner__name">{store.name}</div>
            {store.address && (
              <div className="fh-order-store-banner__meta">{store.address}</div>
            )}
            {[store.phone, store.email].filter(Boolean).length > 0 && (
              <div className="fh-order-store-banner__meta">
                {[store.phone, store.email].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        </div>
        <div className="fh-order-store-banner__right">
          <div className="fh-order-store-banner__label">
            {form.kind === "offer" ? "NEW QUOTATION" : "NEW SALES ORDER"}
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {defaults && (
        <Alert variant="info" className="mb-3">
          <strong>Defaults from Settings → Finance:</strong>
          <ul className="mb-0 mt-1">
            <li>
              Currency: <strong>{defaults.currency}</strong> (
              {defaults.currencySymbol})
            </li>
            <li>
              Tax: <strong>{settingsNote}</strong>
            </li>
            <li>
              Discounts: <strong>{discountNote}</strong>
            </li>
            <li>
              Offer validity: <strong>{defaults.dueDays} days</strong>
            </li>
          </ul>
        </Alert>
      )}

      <Card title="Customer" className="mb-3">
        <CustomerPicker
          value={customer}
          onChange={setCustomer}
          onCreateNew={() => setCustomerModalOpen(true)}
        />
      </Card>

      <Card title="Order details" className="mb-3">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="fh-input-label">Kind</label>
            <select
              className="fh-input"
              value={form.kind}
              onChange={(e) => set("kind", e.target.value)}
            >
              <option value="offer">Offer (quotation)</option>
              <option value="order">Order (internal)</option>
            </select>
          </div>
          <div className="col-md-3">
            <Input
              label="Currency"
              value={form.currency}
              onChange={(e) => set("currency", e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <Input
              label="Title / Subject"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <Input
              label="Valid Until"
              type="date"
              value={form.validUntil}
              onChange={(e) => set("validUntil", e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <Input
              label={`Discount %${defaults ? ` (max ${defaults.maxDiscount})` : ""}`}
              type="number"
              step="0.1"
              min={0}
              max={defaults?.maxDiscount ?? 100}
              value={form.discountPercent}
              onChange={(e) => handleDiscountChange(e.target.value)}
              disabled={defaults && !defaults.allowManualDiscount}
            />
          </div>
          <div className="col-md-4">
            <Input
              label={`Tax Rate %${
                defaults?.taxEnabled ? ` (default ${defaults.taxRate})` : ""
              }`}
              type="number"
              step="0.1"
              min={0}
              max={100}
              value={form.taxRate}
              onChange={(e) => set("taxRate", Number(e.target.value) || 0)}
            />
          </div>
          <div className="col-md-4">
            <Input
              label="Shipping"
              type="number"
              step="0.01"
              min={0}
              value={form.shipping}
              onChange={(e) => set("shipping", Number(e.target.value) || 0)}
            />
          </div>
        </div>
      </Card>

      <Card title="Items" className="mb-3">
        <OrderItemsEditor
          items={form.items}
          onChange={(v) => set("items", v)}
          currency={form.currency}
        />
      </Card>

      <Card title="Notes & Terms" className="mb-3">
        <div className="row g-3">
          <div className="col-md-6">
            <Input
              label="Notes (visible to customer)"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <Input
              label="Internal notes"
              value={form.internalNotes}
              onChange={(e) => set("internalNotes", e.target.value)}
            />
          </div>
          <div className="col-12">
            <Input
              label="Terms & Conditions (prefilled from Settings → Finance)"
              value={form.termsAndConditions}
              onChange={(e) => set("termsAndConditions", e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Total
            </div>
            <strong style={{ fontSize: 22 }}>
              {formatMoney(totalAmount, { symbol: form.currency })}
            </strong>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/orders")}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={saving}>
              Create {form.kind === "offer" ? "Offer" : "Order"}
            </Button>
          </div>
        </div>
      </Card>

      <CustomerModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onSaved={(c) => {
          setCustomer(c);
          setCustomerModalOpen(false);
        }}
      />
    </>
  );
}
